import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	InputLabel,
	OutlinedInput,
	Stack,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { EditModalState } from '../../modal/useEditModal';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const updatePersonaMutation = graphql(`
	mutation organization_updatePersona($id: UUID!, $name: String!) {
		updatePersona(by: { id: $id }, data: { name: $name }) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.personas.error.empty-name' })),
	});

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

type Props = {
	readonly state: EditModalState<{ id: string; name: string }>;
	readonly onSave: () => void;
	readonly onCancel: () => void;
};

const PersonaEdit: FC<Props> = ({ state, onSave, onCancel }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { handleSubmit, register, formState, reset } = useTypedForm({
		schema: createFormSchema(intl),
	});

	// sync form initial values
	useEffect(() => {
		if (state.isOpen && state.data != null) {
			reset({
				name: state.data.name,
			});
		}
	}, [state, reset]);

	const { mutate: updatePersona } = useApiMutation(updatePersonaMutation);

	const onSubmit = (values: FormValues) => {
		if (state.data != null) {
			updatePersona(
				{
					id: state.data.id,
					...values,
				},
				{
					onSuccess: (result) => {
						mutationResponseHandler({
							result: result.updatePersona.ok,
							toastMessages: {
								error: intl.formatMessage({ id: 'persona.create.error' }),
							},
							errorMessage: result.updatePersona.errorMessage,
							callback: onSave,
							openSnackbar,
						});
					},
				},
			);
		}
	};

	return (
		<Dialog maxWidth={'xs'} keepMounted fullWidth onClose={onCancel} open={state.isOpen}>
			<form noValidate onSubmit={handleSubmit(onSubmit)}>
				<DialogTitle>
					<FormattedMessage id={'form.persona.title.update'} />
				</DialogTitle>
				<Divider />
				<DialogContent>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Stack spacing={1}>
								<InputLabel htmlFor={'name'}>
									<FormattedMessage id={'form.persona.label.name'} />
								</InputLabel>
								<OutlinedInput {...register('name')} id={'name'} />
								{formState.errors.name != null && <FormHelperText error>{formState.errors.name.message}</FormHelperText>}
							</Stack>
						</Grid>

						{formState.errors.root != null && (
							<Grid item xs={12}>
								<FormHelperText error>{formState.errors.root.message}</FormHelperText>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ p: 2.5 }}>
					<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
						<Button color={'secondary'} onClick={onCancel}>
							<FormattedMessage id={'common.cancel'} />
						</Button>
						<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting}>
							<FormattedMessage id={'common.save'} />
						</Button>
					</Stack>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export { PersonaEdit };
